import { cloneDeep } from "lodash";

import { config } from "@krea/shared/config";
import { getGtmId } from "@krea/shared/constants/gtm";
import { LINKS } from "@krea/shared/constants/links";
import { CountryCode } from "@krea/shared/types/common";

import { useCustomerFeaturesSettingsStore } from "./store/app-settings/useCustomerFeaturesSettingsStore";
import type { AppSettingsProps, SettingsContactsProps } from "./types/settings";

const environment = config.environment;

const getGtmID = () =>
  getGtmId(useCustomerFeaturesSettingsStore.getState().countryCode);

const getCountryWebsiteUrl = () => {
  const countryCode = useCustomerFeaturesSettingsStore.getState().countryCode;
  // Defensive: the store could hold a country the catalog does not know.
  const url = config.appUrls(countryCode)?.website;

  if (!url) {
    throw Error("countryWebsiteUrl cant be null, not configured correct.");
  }

  return url;
};

const getContacts = (): SettingsContactsProps => {
  const countryCode = useCustomerFeaturesSettingsStore.getState().countryCode;

  switch (countryCode) {
    case CountryCode.SE:
      return {
        phone: {
          value: "08-551 082 62",
          link: "+46855108262",
        },
        email: "kundservice@krea.se",
        adress: {
          value: "Valhallavägen 60, 114 27 Stockholm",
          link: LINKS.maps.stockholm,
        },
        openingHours: [
          {
            day: `footer.mondayToThursdayLabel.${countryCode}`,
            time: "09 - 18",
          },
          { day: "footer.fridayLabel", time: "09 - 17" },
        ],
      };

    case CountryCode.FI:
      return {
        phone: {
          value: "09 315 316 10",
          link: "+358931531610",
        },
        email: "asiakaspalvelu@kredita.fi",
        adress: {
          value: "Linnoitustie 6, 02600 Espoo",
          link: LINKS.maps.espoo,
        },
        openingHours: [
          {
            day: `footer.mondayToThursdayLabel.${countryCode}`,
            time: "10 - 16",
          },
        ],
      };

    default:
      throw new Error("There is no contact information for this country");
  }
};

const getAppTitle = () => {
  const countryCode = useCustomerFeaturesSettingsStore.getState().countryCode;

  switch (countryCode) {
    case CountryCode.SE:
      return "Krea";
    case CountryCode.FI:
      return "Kredita";
    default:
      return "Krea";
  }
};

export let settings: AppSettingsProps = {
  contacts: getContacts(),
  countryWebsiteUrl: getCountryWebsiteUrl(),
  gtmId: getGtmID(), // Google tagmanager Id
  appTitle: getAppTitle(),
  defaultListLimit: 25,
  backendBaseUrl: config.apiRootUrl,
  environment,
  customerWebUrl: undefined,
  unauthorizedPaths: [],
};

export const initCommonSettings = (
  appSpecificSettings: Partial<AppSettingsProps>,
): AppSettingsProps => {
  settings = {
    ...settings,
    ...cloneDeep(appSpecificSettings),
  };

  return settings;
};
